// a library to wrap and simplify api calls
import apisauce, { ApiResponse } from 'apisauce';
import debugConfig from '../../../config/debug.config';
import fixtureApiService from './fixture.api.service';
import { SERVER_BASE_URL } from '../../../config/routes-config';
import { ErrorInfo, isErrorInfo } from '../../../interfaces/error-info.interface';
import { UserAuthModel } from '../../../models/user-auth.model';
import {
  cleanupUserData,
  getUserAccessToken,
  getUserRefreshToken,
  setUserToken
} from '../../helpers/auth-helper/auth-helper.service';
import { ResponseModel } from '../../../interfaces/response-model.interface';
import { AreaModel } from '../../../models/area.model';
import { CityModel } from '../../../models/city.model';
import { PropertyModel } from '../../../models/property.model';
import { BulkUpdate, Expenses, UnitTypeModel } from '../../../models/unit-types.model';
import { AmenityModel } from '../../../models/amenity.model';
import { BlogModel } from '../../../models/blog.model';
import { UserModel } from '../../../models/user.model';
import {
  ImportResultModel,
  ImportRowResultModel,
  LandlordDetailModel,
  LandlordListModel
} from '../../../models/landlord.model';
import {
  BookingRequestModel,
  BookingRequestModelData
} from '../../../models/booking-request.model';
import {
  ReservationModel,
  CreateReservationRequestModel,
  ReservationModelData
} from '../../../models/reservation.model';
import { ReservationDetailsModel } from '../../../models/reservation-details.model';
import { GuestEmailType } from '../../../enums/guest-email-type.enum';
import {
  CreateBlockDatesModel,
  BulkBlockDatesResponseModel,
  BlockDatesModel,
  UpdateBlockDatesModel
} from '../../../models/block-dates.model';
import { GuestModel } from '../../../models/guest.model';
import { GuestUiModel } from '../../../ui-models/guest.ui-model';
import { ReservationDetailsUiModel } from '../../../ui-models/reservation.ui-model';
import { PaymentLinkModel, PaymentUIModel } from '../../../ui-models/payment.ui-model';
import { PaymentModel, PaymentModelData, PaymentSearch } from '../../../models/payment.model';
import {
  BookAvailabilityResponseModel,
  BookOfferResponseModel
} from '../../../ui-models/book.ui-model';
import { UnitTypeGroupModel } from '../../../models/unit-type-group..model';
import { ReservationUpdates } from '../../../ui-models/reservation-updates.ui-model';
import { OccupancyResponseModel } from '../../../ui-models/occupancy.ui-model';
import {
  OccupancyDatasetModel,
  OccupancyDatasetQueryModel
} from '../../../models/occupancy-dashboard.model';
import { UserNotificationsModel } from '../../../models/user-notifications.model';
import {
  BulkPayoutResult,
  BulkPayoutRow,
  Payout,
  SearchPayOutDto
} from '../../../models/payout.model';
import { ReservationActivityModel } from '../../../models/reservation-activity.model';
import { AmenityChannelModel } from '../../../models/amenity-channel.model';
import { ConfigurationModel } from '../../../models/configuration.model';
import { ConfigurationUiModel } from '../../../ui-models/configuration.ui.model';
import { CompanyModel } from '../../../models/company.model';
import { BrokerModel } from '../../../models/broker.model';
import {
  LeadActivityModel,
  LeadCommentModel,
  LeadConfigModel,
  LeadModel,
  LeadSearchQueryModel,
  LeadImportTemplateModel,
  LeadImportRequestModel,
  LeadImportSummaryModel,
  LeadBulkAssignSummaryModel
} from '../../../models/lead.model';
import { OpsFilterModel } from '../../../models/lead-ops.model';
import {
  CreateTicketPayload,
  TicketAttachmentPayload,
  TicketCategoriesModel,
  TicketDetailModel,
  TicketModel,
  TicketPriority,
  TicketView
} from '../../../models/ticket.model';
import { TicketStatsModel, TicketStatsPeriod } from '../../../models/ticket-stats.model';
import {
  AccessControlFilterConfigModel,
  AssignmentLimitModel,
  AssignmentRuleModel,
  TeamMemberChangeModel,
  TeamMemberNodeModel,
  TeamModel
} from '../../../models/team.model';
import { FinancialReportQueryModel } from '../../../models/financial-report-query.model';
import { UserType } from '../../../enums/user-type.enum';
import { userRefreshToken } from '../../data/auth.data';
import {
  UnitTypeUiModelResponse,
  RoomsUiModelResponse
} from '../../../ui-models/unit-type.ui-model';
import { KPIChartsSearch } from '../../../models/kpis.model';
import { FeedbackPayload, FeedbackResponse } from '../../../models/feedbacks.model';
import type { PackageModel, FurnishingModel } from '../../../../FurnishingModule/types';
import {
  UnitHistoryRequestModel,
  UnitHistoryResponseModel
} from '../../../models/unit-history.model';
import {
  DailyAvailabilityQueryModel,
  DailyAvailabilityResponseModel
} from '../../../models/daily-availability.model';
import {
  LandlordDetailedReportResponseModel,
  LandlordDetailedReportSearchModel,
  LandlordReportFilterOptionsModel
} from '../../../models/landlord-detailed-report.model';
import {
  GuestAudienceFilterOptionsModel,
  GuestAudienceResponseModel,
  GuestAudienceSearchModel
} from '../../../models/guest-audience.model';
import {
  HomeownerAudienceFilterOptionsModel,
  HomeownerAudienceResponseModel,
  HomeownerAudienceSearchModel
} from '../../../models/homeowner-audience.model';
import {
  OverduePaymentsFilterOptionsModel,
  OverduePaymentsResponseModel,
  OverduePaymentsSearchModel
} from '../../../models/overdue-payments.model';

const create = (baseURL = SERVER_BASE_URL) => {
  const api = apisauce.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json'
    },
    timeout: 240000
  });

  api.addRequestTransform(async (request) => {
    request.httpsAgent = {
      rejectUnauthorized: false
    };
    const jwt = getUserAccessToken();
    if (jwt) request.headers.Authorization = `Bearer ${jwt}`;
  });

  api.addAsyncResponseTransform(async (response) => {
    if (
      response.status === 401 &&
      response.config?.url &&
      response.config?.url?.indexOf('/auth') === -1
    ) {
      const refreshToken = await getUserRefreshToken();
      if (refreshToken) {
        const userData = await userRefreshToken(refreshToken);

        if (isErrorInfo(userData)) {
          cleanupUserData();
        } else {
          setUserToken({ ...userData, refreshToken });
          const accessToken = userData.accessToken;
          if (accessToken) api.setHeader('Authorization', `Bearer ${accessToken}`);
          const data = await api.any(response.config as any);
          response.data = data.data;
          response.status = data.status;
          response.problem = data.problem;
          response.headers = data.headers;
          response.ok = data.ok;
        }
      } else {
        cleanupUserData();
      }
    }
  });

  // ==================== API Functions ==================

  const getRoot = () => api.get('');

  // User & Auth APIs
  const registerUser = (
    email: string,
    password: string
  ): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/register', { email, password });

  const loginUser = (
    email: string,
    password: string,
    token: string | undefined | null
  ): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/login', { email, password, token });

  const changePassword = (
    token: string,
    password: string
  ): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/changePassword', { token, password });

  const forgotPassword = (email: string): Promise<ApiResponse<{}, ErrorInfo>> =>
    api.post('/auth/forgotPassword', { email });

  const validateToken = (): Promise<ApiResponse<{}, ErrorInfo>> => api.get('/auth/verifyToken');

  const refreshToken = (refreshToken: string): Promise<ApiResponse<UserAuthModel, ErrorInfo>> =>
    api.post('/auth/refreshToken', { refreshToken });

  const getUserNotifications = (): Promise<
    ApiResponse<ResponseModel<UserNotificationsModel>, ErrorInfo>
  > => api.get('/notifications/attention');

  // Area APIs
  const getAreas = (): Promise<ApiResponse<ResponseModel<AreaModel[]>, ErrorInfo>> =>
    api.get('/areas/');

  const getCites = (): Promise<ApiResponse<ResponseModel<CityModel[]>, ErrorInfo>> =>
    api.get('/cities');

  const updateCity = (
    cityId: string,
    cityData: Pick<CityModel, 'name' | 'nameAr'>
  ): Promise<ApiResponse<ResponseModel<CityModel>, ErrorInfo>> =>
    api.put('/cities/' + cityId, cityData);

  const createArea = (
    areaData: AreaModel
  ): Promise<ApiResponse<ResponseModel<AreaModel>, ErrorInfo>> => api.post('/areas/', areaData);

  const updateArea = (
    areaId: string,
    areaData: AreaModel
  ): Promise<ApiResponse<ResponseModel<AreaModel>, ErrorInfo>> =>
    api.put('/areas/' + areaId, areaData);

  const deleteArea = (areaId: string): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete('/areas/' + areaId);

  const uploadDefaultImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<AreaModel>, ErrorInfo>> =>
    api.post(`/areas/uploadDefaultImage`, formData);

  const uploadSponsoredImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<AreaModel>, ErrorInfo>> =>
    api.post(`/areas/uploadSponsoredImage`, formData);

  // Property APIs
  const getProperties = (): Promise<ApiResponse<ResponseModel<PropertyModel[]>, ErrorInfo>> =>
    api.post('/properties/search', {});

  const getProperty = (
    propertyId: string
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.get('/properties/' + propertyId);

  const createProperty = (
    propertyData: PropertyModel
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.post('/properties/', propertyData);

  const updateProperty = (
    propertyId: string,
    propertyData: Partial<PropertyModel>
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.put('/properties/' + propertyId, propertyData);

  const deleteProperty = (propertyId: string): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete('/properties/' + propertyId);

  const uploadPropertyAsset = (
    formData: FormData,
    propertyId: string
  ): Promise<ApiResponse<{ data: any }, ErrorInfo>> =>
    api.post(`/properties/uploadAsset/${propertyId}`, formData);

  const uploadPropertyCoverImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.post('/properties/uploadCover', formData);

  const uploadCompoundDetails = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.post('/properties/uploadCompound', formData);

  const deleteCompoundDetails = (
    propertyId: string,
    id: string
  ): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete(`/properties/${propertyId}/compound/${id}`);

  const updateCompoundDetails = (
    propertyId: string,
    id: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.put(`/properties/${propertyId}/compound/${id}`, formData);

  const addPropertyManager = (
    id: string,
    propertyId: string
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.post('properties/addPropertyManagers', { id, propertyId });

  const deletePropertyManager = (
    id: string,
    propertyId: string
  ): Promise<ApiResponse<ResponseModel<PropertyModel>, ErrorInfo>> =>
    api.delete(`properties/deletePropertyManagers?id=${id}&propertyId=${propertyId}`);

  // Unit Type APIs
  const createUnitType = (
    unitTypeData: UnitTypeModel
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/', unitTypeData);

  const getPropertyUnitTypes = (
    propertyId: string
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel[]>, ErrorInfo>> =>
    api.get(`/properties/${propertyId}/unitTypes`);

  const getUnassignedUnitTypes = (): Promise<
    ApiResponse<ResponseModel<UnitTypeModel[]>, ErrorInfo>
  > => api.get('/unitTypes/unassigned');

  const getUnitTypes = (
    limit: string,
    offset: string,
    query: {}
  ): Promise<ApiResponse<ResponseModel<UnitTypeUiModelResponse>, ErrorInfo>> =>
    api.post('/unitTypes/search', { limit, offset, ...query });

  const getAllRooms = (): Promise<ApiResponse<ResponseModel<RoomsUiModelResponse[]>, ErrorInfo>> =>
    api.get('/unitTypes/getAllRooms');

  const searchInUnitTypes = (data: {
    checkInDate?: number;
    checkOutDate?: number;
    landlordId?: string;
  }): Promise<ApiResponse<ResponseModel<UnitTypeModel[]>, ErrorInfo>> =>
    api.post('/unitTypes/search', data);

  const getUnitType = (
    unitTypeId: string
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.get(`/unitTypes/${unitTypeId}`);

  const updateUnitType = (
    unitTypeId: string,
    unitTypeData: Partial<UnitTypeModel>
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.put('/unitTypes/' + unitTypeId, unitTypeData);

  const bulkUpdateUnitType = (
    unitTypeData: Partial<UnitTypeModel[]>,
    actionDescription?: string
  ): Promise<ApiResponse<ResponseModel<BulkUpdate>, ErrorInfo>> =>
    // The body stays the bare array it has always been, and the description rides
    // along as a query param. Wrapping it into { units, ... } made this a hard
    // dependency on a matching backend: an older one reads req.body as an array,
    // so it would create an empty log record and then throw on .map().
    api.put('/unitTypes/bulkUpdate', unitTypeData, {
      params: actionDescription ? { actionDescription } : undefined
    });

  const getAllBulkUpdateUnitTypeData = (params?: {
    offset?: number;
    limit?: number;
  }): Promise<ApiResponse<ResponseModel<BulkUpdate[]>, ErrorInfo>> =>
    api.get('/unitTypes/bulkUpdate', params);

  const syncUnitTypeChannel = (
    unitTypeId: string
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post(`/unitTypes/${unitTypeId}/sync`, {});

  const deleteUnitTypeShowroomImage = (data: {
    unitTypeId: number;
    imageId: string;
    roomId: string;
  }): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/deleteShowRoom', data);

  const uploadUnitTypeShowRoomImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/uploadShowRoomImage', formData);

  const uploadUnitTypeImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/uploadImage', formData);

  const uploadBrandingImage = (
    unitTypeId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post(`/unitTypes/${unitTypeId}/uploadBrandingImages`, formData);

  const deleteBrandingImage = (data: {
    unitTypeId: string;
    imageId: string;
  }): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.delete(`unitTypes/${data.unitTypeId}/brandingImages/${data.imageId}`);

  const deleteUnitTypeImage = (data: {
    unitTypeId: number;
    imageId: string;
  }): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('/unitTypes/deleteImage', data);

  const addPropertyManagerToUnits = (
    id: string,
    unitTypeId: number
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.post('unitTypes/addPropertyManagers', { userId: id, unitTypeId: String(unitTypeId) });

  const deletePropertyManagerToUnits = (
    id: string,
    unitTypeId: number
  ): Promise<ApiResponse<ResponseModel<UnitTypeModel>, ErrorInfo>> =>
    api.delete(`unitTypes/deletePropertyManagers?userId=${id}&unitTypeId=${unitTypeId}`);

  // Amenity APIs
  const getAmenities = (): Promise<ApiResponse<ResponseModel<AmenityModel[]>, ErrorInfo>> =>
    api.get('/amenities/');

  const getChannelAmenities = (): Promise<
    ApiResponse<ResponseModel<AmenityChannelModel[]>, ErrorInfo>
  > => api.get('/amenities/channel/list');

  const getChannelRoomAmenities = (): Promise<
    ApiResponse<ResponseModel<AmenityChannelModel[]>, ErrorInfo>
  > => api.get('/amenities/channel/roomList');

  // ---- Lead platform: leads
  //
  // Paths match the admin BFF, not the lead service. The BFF exposes
  // `/team-members` where the service serves `/teamMember` — that spelling is
  // fixed by a contract with server-common and stops at the BFF.
  const getLeadConfig = (): Promise<ApiResponse<ResponseModel<LeadConfigModel>, ErrorInfo>> =>
    api.get('/leads/config');

  const searchLeads = (
    query: LeadSearchQueryModel,
    limit?: number,
    skip?: number
  ): Promise<ApiResponse<ResponseModel<LeadModel[]>, ErrorInfo>> =>
    api.post('/leads/search', query, { params: { limit, skip } });

  const getLeadById = (leadId: string): Promise<ApiResponse<ResponseModel<LeadModel>, ErrorInfo>> =>
    api.get(`/leads/${leadId}`);

  /**
   * Leads Ops reports. POST because the filter is a nested object with
   * several arrays — see the controller in the lead service.
   */
  const opsReport = <T>(
    report: string,
    query: OpsFilterModel
  ): Promise<ApiResponse<ResponseModel<T>, ErrorInfo>> =>
    api.post(`/leads/report/ops/${report}`, { query });

  const createLead = (
    lead: Partial<LeadModel>
  ): Promise<ApiResponse<ResponseModel<{ id: string }>, ErrorInfo>> => api.post('/leads', lead);

  const getLeadImportTemplate = (
    vertical: string
  ): Promise<ApiResponse<ResponseModel<LeadImportTemplateModel>, ErrorInfo>> =>
    api.get('/leads/import/template', { vertical });

  const importLeads = (
    payload: LeadImportRequestModel
  ): Promise<ApiResponse<ResponseModel<LeadImportSummaryModel>, ErrorInfo>> =>
    api.post('/leads/import', payload);

  const bulkAssignLeads = (
    leadIds: string[],
    assignedTo: string
  ): Promise<ApiResponse<ResponseModel<LeadBulkAssignSummaryModel>, ErrorInfo>> =>
    api.put('/leads/assign/bulk', { leadIds, assignedTo });

  const bulkAutoAssignLeads = (
    leadIds: string[]
  ): Promise<ApiResponse<ResponseModel<LeadBulkAssignSummaryModel>, ErrorInfo>> =>
    api.put('/leads/assign/auto', { leadIds });

  const updateLead = (
    leadId: string,
    lead: Partial<LeadModel>
  ): Promise<ApiResponse<ResponseModel<LeadModel>, ErrorInfo>> => api.put(`/leads/${leadId}`, lead);

  const changeLeadStatus = (
    leadId: string,
    payload: Record<string, unknown>
  ): Promise<ApiResponse<ResponseModel<LeadModel>, ErrorInfo>> =>
    api.put(`/leads/${leadId}/status`, payload);

  const recordContactAttempt = (
    leadId: string,
    payload: { note?: string }
  ): Promise<ApiResponse<ResponseModel<LeadModel>, ErrorInfo>> =>
    api.put(`/leads/${leadId}/contact-attempt`, payload);

  const assignLead = (
    leadId: string,
    assignedTo: string
  ): Promise<ApiResponse<ResponseModel<LeadModel>, ErrorInfo>> =>
    api.put(`/leads/${leadId}/assign`, { assignedTo });

  const markLeadDuplicate = (
    leadId: string,
    duplicateOfLeadId: string
  ): Promise<ApiResponse<ResponseModel<LeadModel>, ErrorInfo>> =>
    api.put(`/leads/${leadId}/duplicate`, { duplicateOfLeadId });

  const getLeadTimeline = (
    leadId: string
  ): Promise<ApiResponse<ResponseModel<LeadActivityModel[]>, ErrorInfo>> =>
    api.get(`/leads/${leadId}/timeline`);

  const getLeadComments = (
    leadId: string
  ): Promise<ApiResponse<ResponseModel<LeadCommentModel[]>, ErrorInfo>> =>
    api.get(`/leads/${leadId}/comments`);

  const createLeadComment = (
    leadId: string,
    text: string
  ): Promise<ApiResponse<ResponseModel<{ id: string }>, ErrorInfo>> =>
    api.post(`/leads/${leadId}/comments`, { text });

  const updateLeadComment = (
    commentId: string,
    text: string
  ): Promise<ApiResponse<ResponseModel<LeadCommentModel>, ErrorInfo>> =>
    api.put(`/leads/comments/${commentId}`, { text });

  const deleteLeadComment = (
    commentId: string
  ): Promise<ApiResponse<ResponseModel<LeadCommentModel>, ErrorInfo>> =>
    api.delete(`/leads/comments/${commentId}`);

  // ---- Tickets (#244): server.integrator.helpdesk via the BFF's /tickets pass-through
  const getTicketCategories = (): Promise<
    ApiResponse<ResponseModel<TicketCategoriesModel>, ErrorInfo>
  > => api.get('/helpdesk/categories');

  /** The performance dashboard: counts for one team and period; the integrator scopes it. */
  const getTicketStats = (
    period: TicketStatsPeriod,
    team?: number
  ): Promise<ApiResponse<ResponseModel<TicketStatsModel>, ErrorInfo>> =>
    api.get('/helpdesk/stats', team ? { period, team } : { period });

  const getTickets = (
    view: TicketView,
    page = 1
  ): Promise<ApiResponse<ResponseModel<TicketModel[]>, ErrorInfo>> =>
    api.get('/tickets', { view, page });

  const getTicket = (
    id: number
  ): Promise<ApiResponse<ResponseModel<TicketDetailModel>, ErrorInfo>> => api.get(`/tickets/${id}`);

  const createTicket = (
    payload: CreateTicketPayload
  ): Promise<ApiResponse<ResponseModel<TicketModel>, ErrorInfo>> => api.post('/tickets', payload);

  const addTicketNote = (
    id: number,
    body: string,
    attachments?: TicketAttachmentPayload[]
  ): Promise<ApiResponse<ResponseModel<TicketDetailModel>, ErrorInfo>> =>
    api.post(`/tickets/${id}/notes`, { body, attachments });

  /** An email to the ticket's guest, sent by Zammad from the team's address. */
  const replyToTicket = (
    id: number,
    body: string,
    attachments?: TicketAttachmentPayload[]
  ): Promise<ApiResponse<ResponseModel<TicketDetailModel>, ErrorInfo>> =>
    api.post(`/tickets/${id}/reply`, { body, attachments });

  /** Categorise a ticket that arrived without one (a guest email). Allowed once. */
  const categorizeTicket = (
    id: number,
    payload: { code: string; emergency?: boolean }
  ): Promise<ApiResponse<ResponseModel<TicketModel>, ErrorInfo>> =>
    api.patch(`/tickets/${id}/category`, payload);

  const handoffTicket = (
    id: number,
    payload: { team?: string; reason?: string }
  ): Promise<ApiResponse<ResponseModel<TicketModel>, ErrorInfo>> =>
    api.post(`/tickets/${id}/handoff`, payload);

  const raiseTicketPriority = (
    id: number,
    priority: TicketPriority
  ): Promise<ApiResponse<ResponseModel<TicketModel>, ErrorInfo>> =>
    api.patch(`/tickets/${id}/priority`, { priority });

  const assignTicket = (
    id: number,
    to: 'me' | number
  ): Promise<ApiResponse<ResponseModel<TicketModel>, ErrorInfo>> =>
    api.post(`/tickets/${id}/assign`, { to: String(to) });

  const setTicketState = (
    id: number,
    state: 'open' | 'closed'
  ): Promise<ApiResponse<ResponseModel<TicketModel>, ErrorInfo>> =>
    api.patch(`/tickets/${id}/state`, { state });

  /** The bytes, as a blob: a plain link would not carry the Authorization header. */
  const downloadTicketAttachment = (
    ticketId: number,
    articleId: number,
    attachmentId: number
  ): Promise<ApiResponse<Blob, ErrorInfo>> =>
    api.get(
      `/tickets/${ticketId}/articles/${articleId}/attachments/${attachmentId}`,
      {},
      { responseType: 'blob' }
    );

  // ---- Lead platform: teams and the reporting tree
  const searchTeams = (
    query: Record<string, unknown> = {}
  ): Promise<ApiResponse<ResponseModel<TeamModel[]>, ErrorInfo>> =>
    api.post('/teams/search', query);

  const getTeamById = (teamId: string): Promise<ApiResponse<ResponseModel<TeamModel>, ErrorInfo>> =>
    api.get(`/teams/${teamId}`);

  const createTeam = (
    team: Partial<TeamModel>
  ): Promise<ApiResponse<ResponseModel<{ id: string }>, ErrorInfo>> => api.post('/teams', team);

  const updateTeam = (
    teamId: string,
    team: Partial<TeamModel>
  ): Promise<ApiResponse<ResponseModel<TeamModel>, ErrorInfo>> => api.put(`/teams/${teamId}`, team);

  const deleteTeam = (teamId: string): Promise<ApiResponse<ResponseModel<TeamModel>, ErrorInfo>> =>
    api.delete(`/teams/${teamId}`);

  const getAccessControlConfig = (): Promise<
    ApiResponse<ResponseModel<AccessControlFilterConfigModel>, ErrorInfo>
  > => api.get('/teams/access-control-config');

  const getTeamMemberTree = (
    filter: Record<string, unknown> = {}
  ): Promise<ApiResponse<ResponseModel<TeamMemberNodeModel[]>, ErrorInfo>> =>
    api.post('/team-members/tree', filter);

  const searchTeamMembers = (
    query: Record<string, unknown> = {}
  ): Promise<ApiResponse<ResponseModel<TeamMemberNodeModel[]>, ErrorInfo>> =>
    api.post('/team-members/search', query);

  const createTeamMember = (
    member: Record<string, unknown>
  ): Promise<ApiResponse<ResponseModel<{ id: string }>, ErrorInfo>> =>
    api.post('/team-members', member);

  const updateTeamMember = (
    userId: string,
    member: Record<string, unknown>
  ): Promise<ApiResponse<ResponseModel<TeamMemberNodeModel>, ErrorInfo>> =>
    api.put(`/team-members/${userId}`, member);

  const archiveTeamMember = (
    userId: string
  ): Promise<ApiResponse<ResponseModel<{ id: string }>, ErrorInfo>> =>
    api.delete(`/team-members/${userId}`);

  const recoverTeamMember = (
    userId: string
  ): Promise<ApiResponse<ResponseModel<{ id: string }>, ErrorInfo>> =>
    api.put(`/team-members/recover/${userId}`, {});

  const getTeamMemberHistory = (
    userId: string
  ): Promise<ApiResponse<ResponseModel<TeamMemberChangeModel[]>, ErrorInfo>> =>
    api.get(`/team-members/${userId}/history`);

  // ---- Lead platform: routing configuration
  const getAssignmentLimits = (): Promise<
    ApiResponse<ResponseModel<AssignmentLimitModel[]>, ErrorInfo>
  > => api.get('/assignment-limits');

  const upsertAssignmentLimit = (payload: {
    role: string;
    maxActiveLeads: number | null;
  }): Promise<ApiResponse<ResponseModel<AssignmentLimitModel>, ErrorInfo>> =>
    api.put('/assignment-limits', payload);

  const getAssignmentRules = (): Promise<
    ApiResponse<ResponseModel<AssignmentRuleModel[]>, ErrorInfo>
  > => api.get('/assignment-rules');

  const getRuleCriteriaKeys = (): Promise<ApiResponse<ResponseModel<string[]>, ErrorInfo>> =>
    api.get('/assignment-rules/criteria-keys');

  const createAssignmentRule = (
    rule: Partial<AssignmentRuleModel>
  ): Promise<ApiResponse<ResponseModel<{ id: string }>, ErrorInfo>> =>
    api.post('/assignment-rules', rule);

  const updateAssignmentRule = (
    ruleId: string,
    rule: Partial<AssignmentRuleModel>
  ): Promise<ApiResponse<ResponseModel<AssignmentRuleModel>, ErrorInfo>> =>
    api.put(`/assignment-rules/${ruleId}`, rule);

  const deleteAssignmentRule = (
    ruleId: string
  ): Promise<ApiResponse<ResponseModel<AssignmentRuleModel>, ErrorInfo>> =>
    api.delete(`/assignment-rules/${ruleId}`);

  // Broker APIs
  const getBrokers = (): Promise<ApiResponse<ResponseModel<BrokerModel[]>, ErrorInfo>> =>
    api.get('/broker');

  const createBroker = (
    brokerData: BrokerModel
  ): Promise<ApiResponse<ResponseModel<BrokerModel>, ErrorInfo>> => api.post('/broker', brokerData);

  const updateBroker = (
    brokerId: number,
    brokerData: Partial<BrokerModel>
  ): Promise<ApiResponse<ResponseModel<BrokerModel>, ErrorInfo>> =>
    api.put(`/broker/${brokerId}`, brokerData);

  const deleteBroker = (brokerId: number): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete(`/broker/${brokerId}`);

  // Company APIs
  const getCompanies = (): Promise<ApiResponse<ResponseModel<CompanyModel[]>, ErrorInfo>> =>
    api.get('/company');

  const createCompany = (
    companyData: CompanyModel
  ): Promise<ApiResponse<ResponseModel<CompanyModel>, ErrorInfo>> =>
    api.post('/company', companyData);

  const updateCompany = (
    companyId: string,
    companyData: Partial<CompanyModel>
  ): Promise<ApiResponse<ResponseModel<CompanyModel>, ErrorInfo>> =>
    api.put(`/company/${companyId}`, companyData);

  const deleteCompany = (companyId: string): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete(`/company/${companyId}`);

  const createAmenity = (
    amenityData: AmenityModel
  ): Promise<ApiResponse<ResponseModel<AmenityModel>, ErrorInfo>> =>
    api.post('/amenities/', amenityData);

  const updateAmenity = (
    amenityId: string,
    amenityData: AmenityModel
  ): Promise<ApiResponse<ResponseModel<AmenityModel>, ErrorInfo>> =>
    api.put('/amenities/' + amenityId, amenityData);

  const deleteAmenity = (amenityId: string): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete('/amenities/' + amenityId);

  const uploadAmenityImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<AmenityModel>, ErrorInfo>> =>
    api.post('/amenities/uploadImage', formData);

  // Blog APIs
  const getBlogs = (
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<BlogModel[]>, ErrorInfo>> =>
    api.get(`/blogs/?offset=${offset}&limit=${limit}`);

  const getBlog = (blogId: string): Promise<ApiResponse<ResponseModel<BlogModel>, ErrorInfo>> =>
    api.get('/blogs/' + blogId);

  const createBlog = (
    blogData: BlogModel
  ): Promise<ApiResponse<ResponseModel<BlogModel>, ErrorInfo>> => api.post('/blogs/', blogData);

  const updateBlog = (
    blogId: string,
    blogData: BlogModel
  ): Promise<ApiResponse<ResponseModel<BlogModel>, ErrorInfo>> =>
    api.put('/blogs/' + blogId, blogData);

  const publishBlog = (blogId: string): Promise<ApiResponse<ResponseModel<BlogModel>, ErrorInfo>> =>
    api.put(`/blogs/${blogId}/publish`, {});

  const unpublishBlog = (
    blogId: string
  ): Promise<ApiResponse<ResponseModel<BlogModel>, ErrorInfo>> =>
    api.put(`/blogs/${blogId}/unpublish`, {});

  const deleteBlog = (blogId: string): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete('/blogs/' + blogId);

  const uploadBlogImage = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<BlogModel>, ErrorInfo>> =>
    api.post('/blogs/uploadImage', formData);

  // User Management APIs
  const getAllUsers = (): Promise<ApiResponse<ResponseModel<UserModel[]>, ErrorInfo>> =>
    api.get('/users/');

  const addUser = (
    userModel: UserModel
  ): Promise<ApiResponse<ResponseModel<UserModel>, ErrorInfo>> => api.post('/users/', userModel);

  /**
   * Create a back-office (staff) user. Distinct from addUser above, which posts to /users/ —
   * the public create path that takes no password. /users/admin takes one and is the only route
   * that can set a staff role.
   */
  const createAdminUser = (payload: {
    name: string;
    email: string;
    phone: string;
    password: string;
    role: string;
  }): Promise<ApiResponse<ResponseModel<UserModel>, ErrorInfo>> =>
    api.post('/users/admin', { ...payload, roles: [payload.role] });

  const getSalesPersons = (): Promise<ApiResponse<ResponseModel<UserModel[]>, ErrorInfo>> =>
    api.get('/sales-persons');

  const getLandlords = (query: {
    page: number;
    pageSize: number;
    search?: string;
    bankDetailsMissing?: boolean;
    sortBy?: string;
    sortDirection?: string;
  }): Promise<ApiResponse<ResponseModel<LandlordListModel>, ErrorInfo>> =>
    api.get('/landlords', query);

  const getLandlordDetail = (
    landlordId: string
  ): Promise<ApiResponse<ResponseModel<LandlordDetailModel>, ErrorInfo>> =>
    api.get(`/landlords/${landlordId}`);

  const upsertLandlordBankAccount = (
    landlordId: string,
    bankAccount: {
      bankName: string;
      accountHolderName: string;
      accountNumber: string;
      swiftBic?: string;
      country?: string;
      currency?: string;
    }
  ): Promise<ApiResponse<ResponseModel<unknown>, ErrorInfo>> =>
    api.put(`/landlords/${landlordId}/bank-account`, bankAccount);

  const downloadBankDetailsTemplate = (): Promise<ApiResponse<Blob, ErrorInfo>> =>
    api.get('/landlords/bank-details/template', {}, { responseType: 'blob' });

  const validateBankDetailsImport = (
    file: File
  ): Promise<ApiResponse<ResponseModel<ImportResultModel>, ErrorInfo>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/landlords/bank-details/validate', formData);
  };

  const applyBankDetailsImport = (
    file: File
  ): Promise<ApiResponse<ResponseModel<ImportResultModel>, ErrorInfo>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/landlords/bank-details/import', formData);
  };

  const downloadBankDetailsResult = (
    rows: ImportRowResultModel[],
    format: 'xlsx' | 'csv'
  ): Promise<ApiResponse<Blob, ErrorInfo>> =>
    api.post('/landlords/bank-details/result', { rows, format }, { responseType: 'blob' });

  const createSalesPerson = (
    userModel: UserModel
  ): Promise<ApiResponse<ResponseModel<UserModel>, ErrorInfo>> =>
    api.post('/sales-persons', userModel);

  const updateUser = (user: UserModel): Promise<ApiResponse<ResponseModel<UserModel>, ErrorInfo>> =>
    api.put(`/users`, user);

  const searchUser = (
    queryData: {
      role?: UserType[];
      phone?: string[];
      email?: string[];
      name?: string;
    },
    // Optional: the existing callers (User Search, the assign-manager modals, housekeeping)
    // send none of these and rely on getting the whole result set back.
    pagination?: { offset?: number; limit?: number; export?: boolean }
  ): Promise<ApiResponse<ResponseModel<UserModel[]>, ErrorInfo>> =>
    api.post('/users/search', { query: queryData, ...(pagination || {}) });

  const getUserLockStatus = (
    userId: string
  ): Promise<ApiResponse<ResponseModel<{ data: { isEmailLocked: boolean } }>, ErrorInfo>> =>
    api.get(`users/lockStatus/${userId}`);

  const unlockUser = (userId: string): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.delete(`users/unlock/${userId}`);

  const getUsers = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/reservations/distinct-users', { query, offset, limit });

  // Booking APIs
  const getBookingRequests = (): Promise<
    ApiResponse<ResponseModel<BookingRequestModel[]>, ErrorInfo>
  > => api.get('/bookings/requests');

  const updateBookingRequests = (
    requestId: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel[]>, ErrorInfo>> =>
    api.put(`/bookings/${requestId}`, data);

  const getBookingRequestsByQuery = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<BookingRequestModelData>, ErrorInfo>> =>
    api.post('/bookings/search', { query, offset, limit });

  const getUserBookings = (
    userId: string
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel[]>, ErrorInfo>> =>
    api.post('bookings/bookingsByUserId', { userId });

  const getBookingRequestDetails = (
    requestId: string
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel>, ErrorInfo>> =>
    api.get('/bookings/requests/' + requestId);

  const acceptBookingRequest = (
    requestId: string,
    roomId: string
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel>, ErrorInfo>> =>
    api.post('/bookings/acceptRequest', { requestId, roomId });

  const rejectBookingRequest = (
    requestId: string,
    rejectionReason: string
  ): Promise<ApiResponse<ResponseModel<BookingRequestModel>, ErrorInfo>> =>
    api.post('/bookings/rejectRequest', { requestId, rejectionReason });

  const createNewBookingRequest = (data: any) => api.post('/bookings', data);

  const submitBookingMessage = (
    message: string,
    requestId: string
  ): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.post(`/bookings/requests/${requestId}/messages`, { message });

  const getBookAvailability = (query: { unitTypeId?: string }) =>
    api.get<ResponseModel<BookAvailabilityResponseModel[]>, ErrorInfo>(
      `bookings/availability/unitType/${query.unitTypeId}`
    );

  const getBookOffer = (query: {
    unitTypeId?: string;
    checkInDate: string;
    checkOutDate: string;
    discountAmount?: number;
    discountFees?: string[];
    overRideUnitPrice?: number;
    promoCode?: string;
  }) =>
    api.post<ResponseModel<BookOfferResponseModel>, ErrorInfo>(
      `bookings/offer/unitType/${query.unitTypeId}`,
      query
    );

  // Reservation APIs
  const getReservations = (
    query: {},
    offset: number,
    limit: number,
    countOnly?: boolean
  ): Promise<ApiResponse<ResponseModel<ReservationModelData>, ErrorInfo>> =>
    api.post('/reservations/search', { query, offset, limit, countOnly });

  const getReservationGes = (payload: {
    range: 'TODAY' | 'ATTENTION-REQUIRED' | 'UPCOMING';
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/reservations-ges', payload);

  const getCxQueue = (): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/cx-queue');

  const updateCxQueue = (
    id: string,
    payload: {
      outcomeValue: string;
      issueType?: string;
    }
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/cx-queue/${id}`, payload);

  const uploadReservationGesInspectionImages = (
    reservationId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post(`/reservations-ges/${reservationId}/inspectionImages`, formData);

  const getReservationsOperationsSearch = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/reservations-operations/search', { query, offset, limit });

  const createReservationOperationMaintenance = (payload: {
    description: string;
    reservationId: number | string;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/maintenance-requests/', payload);

  const searchMaintenanceRequests = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/maintenance-requests/search', { query, offset, limit });

  const updateMaintenanceRequest = (
    maintenanceRequestId: number | string,
    payload: {
      status?: 'new' | 'in_progress' | 'done';
      comment: string;
    }
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/maintenance-requests/${maintenanceRequestId}`, payload);

  const getOperationDashboardSummary = (): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/operation-dashboard');

  const getReservationsDashboardSummary = (
    timezone: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/reservations/dashboard-summary', { timezone });

  const getReservationsDashboardUpdates = (): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/reservations/dashboard-updates');

  const getDoubleBookings = (): Promise<
    ApiResponse<
      ResponseModel<
        {
          currentReservation: ReservationModel;
          otherReservation?: ReservationModel;
          blockedDate?: BlockDatesModel;
        }[]
      >,
      ErrorInfo
    >
  > => api.get('/reservations/doubleBookings');

  const getUserReservations = (
    userId: string
  ): Promise<ApiResponse<ResponseModel<ReservationModel[]>, ErrorInfo>> =>
    api.post('reservations/resevationsByUserId', { userId });

  const getOneReservation = (
    id: string
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.get(`/reservations/${id}`);

  const updateOneReservation = (
    reservation: Partial<ReservationDetailsUiModel>
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.put(`/reservations/${reservation.id}`, reservation);

  const updateReservationGes = (
    reservationId: string,
    payload: {
      actualCheckInAt?: number;
      actualCheckOutAt?: number;
      isUnitInspected?: boolean;
      isUnitCleaned?: boolean;
    }
  ): Promise<ApiResponse<ResponseModel<ReservationModel>, ErrorInfo>> =>
    api.put(`/reservations/${reservationId}`, payload);

  const updateReservationDates = (
    reservationId: string,
    dates: { checkInDate: number; checkOutDate: number }
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.put(`/reservations/${reservationId}/dates`, dates);

  const splitReservation = (
    reservationId: string,
    dates: { checkInDate?: number; checkOutDate?: number }
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.post(`/reservations/${reservationId}/split`, dates);

  const createReservations = (reservation: CreateReservationRequestModel) =>
    api.post<ResponseModel<ReservationModel>, ErrorInfo>('reservations', reservation);

  const reactivateReservation = (reservationId: string, roomId: string) =>
    api.post<ResponseModel<ReservationModel>, ErrorInfo>(
      `reservations/${reservationId}/reActivate`,
      { roomId }
    );

  const resendReservationGuestEmail = (
    reservationId: string,
    payload: { emailType: GuestEmailType; paymentId?: string }
  ): Promise<ApiResponse<ResponseModel<{ emailType: GuestEmailType; email: string }>, ErrorInfo>> =>
    api.post(`/reservations/${reservationId}/resend-email`, payload);

  const postReservationMessage = (
    reservationId: string,
    message: string
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.post(`/reservations/${reservationId}/message`, { message });

  const getReservationActivities = (
    reservationId: string
  ): Promise<ApiResponse<ResponseModel<ReservationActivityModel[]>, ErrorInfo>> =>
    api.get(`/reservations/${reservationId}/activities`);

  const getPaymentActivities = (
    paymentId: string
  ): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get(`/payments/${paymentId}/changes`);
  const getReservationChanges = (
    reservationId: string
  ): Promise<ApiResponse<ResponseModel<ReservationActivityModel[]>, ErrorInfo>> =>
    api.get(`/reservations/${reservationId}/changes`);

  const reservationRoomSwitch = (
    reservationId: number,
    sourceUnitTypeId: number,
    destinationUnitTypeId: number,
    roomId: string
  ): Promise<ApiResponse<ResponseModel<ReservationDetailsModel>, ErrorInfo>> =>
    api.post(`/reservations/${reservationId}/switch`, {
      currentUnitTypeId: sourceUnitTypeId,
      newUnitTypeId: destinationUnitTypeId,
      newRoomId: roomId,
      skipValidation: true
    });

  const getReservationsUpdates = () =>
    api.get<ResponseModel<ReservationUpdates>, ErrorInfo>(`reservations/updates `);

  const addPropertyManagerToReservations = (
    id: string,
    reservationId: string
  ): Promise<ApiResponse<ResponseModel<ReservationModel>, ErrorInfo>> =>
    api.post('reservations/addPropertyManagers', { id, reservationId });

  const deletePropertyManagerToReservations = (
    id: string,
    reservationId: string
  ): Promise<ApiResponse<ResponseModel<ReservationModel>, ErrorInfo>> =>
    api.delete(`reservations/deletePropertyManagers?id=${id}&reservationId=${reservationId}`);

  const getAllReservationsFeebacks = (query: FeedbackPayload) =>
    api.post<ResponseModel<{ count: number; data: FeedbackResponse[] }>, ErrorInfo>(
      `reservations/feedback/search`,
      query
    );

  const getOneReservationsFeebacks = (reservationId: string) =>
    api.get<ResponseModel<FeedbackResponse>, ErrorInfo>(`reservations/feedback/${reservationId}`);

  const downloadFeedback = (feedbackId: string) =>
    api.get<ResponseModel<FeedbackResponse>, ErrorInfo>(
      `reservations/feedback/${feedbackId}/export`
    );

  // Guest APIs
  const getOneGuest = (id: string): Promise<ApiResponse<ResponseModel<GuestModel>, ErrorInfo>> =>
    api.get(`/guests/${id}`);

  const updateGuest = (
    guest: GuestUiModel
  ): Promise<ApiResponse<ResponseModel<GuestModel>, ErrorInfo>> =>
    api.put(`/guests/${guest.id}`, guest);

  const deleteGuest = (
    guestId: string
  ): Promise<ApiResponse<ResponseModel<GuestModel>, ErrorInfo>> => api.delete(`/guests/${guestId}`);

  const addGuest = (
    guest: GuestUiModel
  ): Promise<ApiResponse<ResponseModel<GuestModel>, ErrorInfo>> => api.post('/guests', guest);

  const uploadGuestDocument = (
    formData: FormData
  ): Promise<ApiResponse<{ data: any }, ErrorInfo>> => api.post('guests/uploadDocument', formData);

  const getGuests = (
    query: {},
    offset: number,
    limit: number
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/guests/filter', { query, offset, limit });

  // Payment APIs
  const updateOnePayment = (
    payment: PaymentUIModel
  ): Promise<ApiResponse<ResponseModel<PaymentModel>, ErrorInfo>> =>
    api.put(`/payments/${payment.id}`, payment);

  const createOnePayment = (
    payment: PaymentUIModel
  ): Promise<ApiResponse<ResponseModel<PaymentModel>, ErrorInfo>> => api.post('/payments', payment);

  const generatePaymentLink = (
    paymentLink: PaymentLinkModel
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/payments/generatePaymentLink', paymentLink);

  const sendPaymentLink = (
    paymentId: string,
    email: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/payments/sendPaymentLink', { paymentId, email });

  const movePayment = (
    paymentId: string,
    targetReservationId: string,
    comment: string
  ): Promise<ApiResponse<ResponseModel<PaymentModel>, ErrorInfo>> =>
    api.post(`/payments/${paymentId}/move`, { targetReservationId, comment });

  const createPaymentInvoice = (
    paymentId: string
  ): Promise<ApiResponse<ResponseModel<PaymentModel>, ErrorInfo>> =>
    api.post(`/payments/${paymentId}/invoice`, {});

  const checkPaymobPaymentStatus = (
    paymentId: string
  ): Promise<ApiResponse<ResponseModel<PaymentModel>, ErrorInfo>> =>
    api.get(`/payments/${paymentId}/checkPaymobStatus`);

  const uploadPaymentReceipt = (
    formData: FormData
  ): Promise<ApiResponse<{ data: any }, ErrorInfo>> =>
    api.post('reservations/uploadReceipt', formData);

  const paymentSearch = (query: Partial<PaymentSearch>, offset: number, limit: number) =>
    api.post<ResponseModel<PaymentModelData>, ErrorInfo>(`payments/search`, {
      query,
      offset,
      limit
    });

  const financialReportSearch = (query: FinancialReportQueryModel) =>
    api.post<any[], ErrorInfo>(`payments/generatePaymentReport`, query);

  const getUserPayments = (userId: string) =>
    api.post<ResponseModel<PaymentModel[]>, ErrorInfo>(`payments/paymentByUserId`, { userId });

  // Block Dates APIs
  const createBlockDates = (blockDates: CreateBlockDatesModel) =>
    api.post<ResponseModel<BlockDatesModel>, ErrorInfo>('blocked-dates', blockDates);

  const bulkUpdateBlockDates = (blockDates: CreateBlockDatesModel) =>
    api.post<ResponseModel<BulkBlockDatesResponseModel>, ErrorInfo>(
      'blocked-dates/bulk',
      blockDates
    );

  const updateBlockDates = (id: number, blockDate: UpdateBlockDatesModel) =>
    api.put<ResponseModel<BlockDatesModel>, ErrorInfo>('blocked-dates/' + id, blockDate);

  const getBlockDates = (query: {}, offset: number, limit: number) =>
    api.post<ResponseModel<BlockDatesModel>, ErrorInfo>('blocked-dates/search', {
      ...query,
      offset,
      limit
    });

  const getUnitHistory = (request: UnitHistoryRequestModel) =>
    api.post<ResponseModel<UnitHistoryResponseModel>, ErrorInfo>(
      'unit-history/by-short-name',
      request
    );

  // Unit Type Group APIs
  const getUnitTypeGroups = (): Promise<
    ApiResponse<ResponseModel<UnitTypeGroupModel[]>, ErrorInfo>
  > => api.get('/UnitTypeGroups/');

  const createUnitTypeGroup = (
    areaData: UnitTypeGroupModel
  ): Promise<ApiResponse<ResponseModel<UnitTypeGroupModel>, ErrorInfo>> =>
    api.post('/UnitTypeGroups/', areaData);

  const updateUnitTypeGroup = (
    unitTypeGroupModelId: string,
    unitTypeGroupModelData: UnitTypeGroupModel
  ): Promise<ApiResponse<ResponseModel<UnitTypeGroupModel>, ErrorInfo>> =>
    api.put('/UnitTypeGroups/' + unitTypeGroupModelId, unitTypeGroupModelData);

  const deleteUnitTypeGroup = (
    unitTypeGroupModelId: string
  ): Promise<ApiResponse<ResponseModel<{}>, ErrorInfo>> =>
    api.delete('/UnitTypeGroups/' + unitTypeGroupModelId);

  const getUnitViews = (): Promise<
    ApiResponse<ResponseModel<{ id: string; name: string }[]>, ErrorInfo>
  > => api.get('/views/');

  const getTypeOfUnit = (): Promise<
    ApiResponse<ResponseModel<{ id: string; name: string }[]>, ErrorInfo>
  > => api.get('/type-of-units/');

  const getBuilding = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/buildings/');

  // Report APIs
  const getMonthlyRevenue = (monthlycount: number) =>
    api.get<ResponseModel<BookAvailabilityResponseModel[]>, ErrorInfo>(
      `report/monthlyRevenue?months=${monthlycount}`
    );

  const getMonthlyRevenueWithCurrency = (monthlyCount: number, payload: KPIChartsSearch) =>
    api.post<
      ResponseModel<
        { month: string; egpRevenue: number; usdRevenue: number; totalRevenu: number }[]
      >,
      ErrorInfo
    >(`report/monthlyRevenue-currency?months=${monthlyCount}`, payload);

  const getOccupancyAndAverageDailyRate = (monthlyCount: number, payload: KPIChartsSearch) =>
    api.post<
      ResponseModel<{ month: string; rate: number; ADR: number; RPAR: number }[]>,
      ErrorInfo
    >(`report/total-occupancy?months=${monthlyCount}`, payload);

  const getRevenuePerArea = (payload: KPIChartsSearch) =>
    api.post<ResponseModel<{ area: string; share: number }[]>, ErrorInfo>(`report/area`, payload);

  const getInternationAndGuest = (payload: KPIChartsSearch) =>
    api.post<ResponseModel<{ egyptians: number; foreigners: number }>, ErrorInfo>(
      `report/nationality`,
      payload
    );

  const getSourceOfBooking = (monthlyCount: number, payload: KPIChartsSearch) =>
    api.post<
      ResponseModel<{ month: string; salesChannels: number; birdnest: number }[]>,
      ErrorInfo
    >(`report/source-of-booking?months=${monthlyCount}`, payload);

  const getOccupancy = () =>
    api.get<ResponseModel<OccupancyResponseModel[]>, ErrorInfo>(`report/occupancy`);

  const getDailyAvailabilityReport = (
    data: DailyAvailabilityQueryModel
  ): Promise<ApiResponse<ResponseModel<DailyAvailabilityResponseModel>, ErrorInfo>> =>
    api.post('/report/daily-availability', data);

  const getLandlordDetailedReport = (
    data: LandlordDetailedReportSearchModel
  ): Promise<ApiResponse<LandlordDetailedReportResponseModel, ErrorInfo>> =>
    api.post('/report/landlord-detailed', data);

  const getGuestAudience = (
    data: GuestAudienceSearchModel
  ): Promise<ApiResponse<GuestAudienceResponseModel, ErrorInfo>> =>
    api.post('/report/guest-audience', data);

  /**
   * Same endpoint as getGuestAudience, with isExport set. The response is a CSV body rather than
   * JSON, so it is typed loosely and handed to the caller to turn into a download.
   */
  const exportGuestAudience = (
    data: GuestAudienceSearchModel
  ): Promise<ApiResponse<any, ErrorInfo>> =>
    api.post('/report/guest-audience', { ...data, isExport: true });

  const getGuestAudienceFilterOptions = () =>
    api.get<ResponseModel<GuestAudienceFilterOptionsModel>, ErrorInfo>(
      '/report/guest-audience/filter-options'
    );

  const getHomeownerAudience = (
    data: HomeownerAudienceSearchModel
  ): Promise<ApiResponse<HomeownerAudienceResponseModel, ErrorInfo>> =>
    api.post('/report/homeowner-audience', data);

  /**
   * Same endpoint as getHomeownerAudience, with isExport set. The response is a CSV body, typed
   * loosely and handed to the caller to turn into a download, as for the guest audience.
   */
  const exportHomeownerAudience = (
    data: HomeownerAudienceSearchModel
  ): Promise<ApiResponse<any, ErrorInfo>> =>
    api.post('/report/homeowner-audience', { ...data, isExport: true });

  const getHomeownerAudienceFilterOptions = () =>
    api.get<ResponseModel<HomeownerAudienceFilterOptionsModel>, ErrorInfo>(
      '/report/homeowner-audience/filter-options'
    );

  const getOverduePayments = (
    data: OverduePaymentsSearchModel
  ): Promise<ApiResponse<OverduePaymentsResponseModel, ErrorInfo>> =>
    api.post('/payments/overdue', data);

  /**
   * Same endpoint as getOverduePayments, with isExport set. The response is a CSV body rather than
   * JSON, so it is asked for as a blob and handed to the caller to turn into a download.
   */
  const exportOverduePayments = (data: OverduePaymentsSearchModel) =>
    api.post<Blob, ErrorInfo>(
      '/payments/overdue',
      { ...data, isExport: true },
      { responseType: 'blob' }
    );

  const getOverduePaymentsFilterOptions = () =>
    api.get<ResponseModel<OverduePaymentsFilterOptionsModel>, ErrorInfo>(
      '/payments/overdue/filter-options'
    );

  const exportLandlordDetailedReport = (data: LandlordDetailedReportSearchModel) =>
    api.post<Blob, ErrorInfo>(
      '/report/landlord-detailed',
      { ...data, isExport: true },
      { responseType: 'blob' }
    );

  const getLandlordReportFilterOptions = () =>
    api.get<ResponseModel<LandlordReportFilterOptionsModel>, ErrorInfo>(
      '/report/landlord-detailed/filter-options'
    );

  // Expense & Payout APIs
  const createExpenses = (expense: FormData) =>
    api.post<ResponseModel<Expenses>, ErrorInfo>(`expenses`, expense);

  const deleteExpenses = (expenseId: string) =>
    api.delete<ResponseModel<Expenses>, ErrorInfo>(`expenses/${expenseId}`);

  const uploadExpense = (expenseFile: FormData) =>
    api.post<ResponseModel<Expenses>, ErrorInfo>(`expenses/uploadReceipt`, expenseFile);

  const getExpenses = (query: {}) =>
    api.post<ResponseModel<Expenses[]>, ErrorInfo>(`expenses/search`, query);

  const createPayout = (payout: FormData) =>
    api.post<ResponseModel<Payout>, ErrorInfo>(`payout`, payout);

  const uploadPayout = (payoutFile: FormData) =>
    api.post<ResponseModel<Payout>, ErrorInfo>(`payout/uploadReceipt`, payoutFile);

  const previewBulkPayouts = (data: FormData) =>
    api.post<ResponseModel<BulkPayoutRow[]>, ErrorInfo>(`payout/bulk/preview`, data);

  const createBulkPayouts = (data: FormData) =>
    api.post<ResponseModel<BulkPayoutResult>, ErrorInfo>(`payout/bulk`, data);

  const deletePayout = (payoutId: string) =>
    api.delete<ResponseModel<Payout>, ErrorInfo>(`payout/${payoutId}`);

  const searchPayouts = (payout: SearchPayOutDto) =>
    api.post<ResponseModel<Payout[]>, ErrorInfo>(`payout/search`, payout);

  const getPayouts = (query: {}) =>
    api.post<ResponseModel<Payout[]>, ErrorInfo>(`payout/search`, query);

  const searchHousekeepingOrders = (query: {
    offset?: number;
    limit?: number;
    project?: string;
    unitId?: string;
    projectId?: string;
    areaId?: string;
    unit?: string;
    orderDateFrom?: number;
    orderDateTo?: number;
  }) => api.post<ResponseModel<any[]>, ErrorInfo>(`/housekeeping-orders/search`, query);

  const updateHousekeepingOrder = (
    orderId: string,
    data: {
      orderDate?: number;
      status?: string;
      houseKeepingIds?: string[];
    }
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/housekeeping-orders/${orderId}`, data);

  const assignHousekeepingOrderToSelf = (
    orderId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/housekeeping-orders/${orderId}/assign-self`, {});

  const assignHousekeepingUsers = (data: {
    unitTypeIds?: string[];
    projectId?: string;
    assignedToId: string;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post(`/housekeeping-orders/assign-users`, data);

  const createHousekeepingOrder = (data: {
    projectId: string;
    unitTypeIds: string[];
    houseKeepingIds?: string[];
    orderDate: number;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post(`/housekeeping-orders`, data);

  //TODO: change this
  const uploadHousekeepingOrderImages = (
    orderId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post(`/housekeeping-orders/${orderId}/Images`, formData);

  // Configuration APIs
  const getConfiguration = (): Promise<
    ApiResponse<ResponseModel<ConfigurationModel[]>, ErrorInfo>
  > => api.get('/config');

  const updateConfiguration = (
    configuration: ConfigurationUiModel[]
  ): Promise<ApiResponse<ResponseModel<ConfigurationModel[]>, ErrorInfo>> =>
    api.put('/config', { configs: configuration });

  // Furnishing APIs
  const furnishingRequestsGet = (params: {
    offset?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/furnishing-requests', params);

  const furnishingRequestUpdate = (
    id: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/furnishing-requests/${id}`, data);

  const furnishingRequestCreate = (
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/furnishing-requests', data);

  const furnishingRequestStatusEventsGet = (
    furnishingRequestId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get(`/furnishing-request-status-events/furnishing-request/${furnishingRequestId}`);

  const furnishingPackagesGet = (): Promise<
    ApiResponse<ResponseModel<PackageModel[]>, ErrorInfo>
  > => api.get('/packages');

  const furnishingModelsGet = (): Promise<
    ApiResponse<ResponseModel<FurnishingModel[]>, ErrorInfo>
  > => api.get('/models');

  const furnishingPackageCreate = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<PackageModel>, ErrorInfo>> =>
    api.post('/packages', formData);

  const furnishingPackageUpdate = (
    id: string,
    formData: any
  ): Promise<ApiResponse<ResponseModel<PackageModel>, ErrorInfo>> =>
    api.put(`/packages/${id}`, formData);

  const furnishingPackageDelete = (
    id: string
  ): Promise<ApiResponse<ResponseModel<PackageModel>, ErrorInfo>> => api.delete(`/packages/${id}`);

  const furnishingModelCreate = (
    modelData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post(`/models`, modelData);

  const furnishingModelUpdate = (
    modelId: string,
    modelData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/models/${modelId}`, modelData);

  const furnishingModelDelete = (
    modelId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/models/${modelId}`);

  const furnishingShowRoomsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/show-rooms');

  const furnishingRoomCreate = (
    showRoomData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/show-rooms', showRoomData);

  const furnishingRoomDelete = (
    showRoomId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/show-rooms/${showRoomId}`);

  const furnishingRoomUpdate = (
    showRoomId: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/show-rooms/${showRoomId}`, data);

  const furnishingItemSpecCreate = (
    itemSpecData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/item-specs', itemSpecData);

  const furnishingItemSpecsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/item-specs');

  const furnishingItemSpecDelete = (
    itemSpecId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/item-specs/${itemSpecId}`);

  const furnishingItemSpecUpdate = (
    itemSpecId: string,
    itemSpecData: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/item-specs/${itemSpecId}`, itemSpecData);

  const furnishingItemSpecImageUpdate = (
    itemSpecId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/item-specs/${itemSpecId}/image`, formData);

  const furnishingPricesCreate = (pricesData: {
    prices: Array<{
      packageId: string;
      modelId: string;
      unitTypeId: number;
      price: number;
      addons?: string;
    }>;
  }): Promise<
    ApiResponse<
      ResponseModel<
        { packageId: string; modelId: string; unitTypeId: number; price: number; addons?: string }[]
      >,
      ErrorInfo
    >
  > => api.post(`/offers`, { offers: pricesData?.prices });

  const furnishingOffersUpdate = (
    offerId: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/offers/${offerId}`, data);

  const furnishingOffersGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/offers');

  const furnishingOffersDelete = (
    offerId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/offers/${offerId}`);

  const furnishingModelImageUpdate = (
    modelId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/models/${modelId}/image`, formData);

  const furnishingPackageImageUpdate = (
    packageId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/packages/${packageId}/image`, formData);

  const furnishingRoomImageUpdate = (
    showRoomId: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/show-rooms/${showRoomId}/images`, formData);

  const furnishingUnitTypes = (): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/unit-types');

  const furnishingUnitTypesWebsite = (): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.get('/unitTypes/group/meta ');

  const furnishingAddOnsCreate = (data: {
    packageId: number;
    unitTypeId: string;
    addons: Array<{ name: string; nameAr: string; price: number; order?: number }>;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/addons', data);

  const furnishingAddOnsUpdate = (
    addonId: string,
    data: { set: { id: string; name: string; nameAr: string; price: number } }
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/addons/${addonId}`, data);

  const furnishingAddOnsDelete = (
    addonId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/addons/${addonId}`);

  const furnishingAddOnsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/addons');

  const furnishingPortfolioGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/portfolio');

  const furnishingPortfolioCreate = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/portfolio', formData);

  const furnishingPortfolioDelete = (
    id: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/portfolio/${id}`);

  const furnishingPortfolioUpdate = (
    id: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/portfolio/${id}`, data);

  const furnishingPortfolioImageUpdate = (
    id: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/portfolio/${id}/image`, formData);

  const furnishingPortfolioRoomsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/portfolio-rooms');

  const furnishingPortfolioRoomsCreate = (
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/portfolio-rooms', formData);

  const furnishingPortfolioRoomsUpdate = (
    id: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.put(`/portfolio-rooms/${id}`, data);

  const furnishingPortfolioRoomsImageUpdate = (
    id: string,
    formData: FormData
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/portfolio-rooms/${id}/image`, formData);

  const furnishingPortfolioReviewsGet = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/portfolio-reviews');

  const furnishingPortfolioReviewCreate = (
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/portfolio-reviews', data);

  const furnishingPortfolioReviewUpdate = (
    id: string,
    data: any
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/portfolio-reviews/${id}`, data);

  const furnishingPortfolioReviewDelete = (
    id: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.delete(`/portfolio-reviews/${id}`);

  const getOccupancyDashboardDataset = (
    data: OccupancyDatasetQueryModel
  ): Promise<ApiResponse<ResponseModel<OccupancyDatasetModel>, ErrorInfo>> =>
    api.post('/report/occupancy-dashboard/dataset', data);

  const calculateUnitAvailabilityDays = (data: {
    from: string;
    to: string;
    areaId?: string;
    propertyId?: string;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.post('/unit-availability-days/calculate', data);

  const getUnitAvailabilityDaysLastRun = (): Promise<
    ApiResponse<ResponseModel<any[]>, ErrorInfo>
  > => api.get('/unit-availability-days/last-run');

  const createPromotion = (data: {
    name: string;
    maximumNumberOfUsage: number;
    maximumNumberOfUsagePerUser: number;
    availableFrom: number;
    availableTo: number;
    dateRanges?: Array<{
      from: number;
      to: number;
    }>;
    type: 'percentage';
    areaIds?: string[];
    cityIds?: string[];
    propertyIds?: string[];
    value: number;
  }): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> => api.post('/promotions', data);

  const getPromotions = (): Promise<ApiResponse<ResponseModel<any[]>, ErrorInfo>> =>
    api.get('/promotions');

  const updatePromotion = (
    promotionId: string,
    data: { isActive?: boolean; isAutomatic?: boolean }
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.put(`/promotions/${promotionId}`, data);

  const deletePromotion = (
    promotionId: string
  ): Promise<ApiResponse<ResponseModel<any>, ErrorInfo>> =>
    api.delete(`/promotions/${promotionId}`);

  // Auto-set displayName for all functions (for error tracking)
  const apiMethods = {
    getRoot,
    registerUser,
    loginUser,
    updateUser,
    changePassword,
    forgotPassword,
    getUserNotifications,
    uploadPropertyAsset,
    uploadPropertyCoverImage,
    uploadCompoundDetails,
    deleteCompoundDetails,
    updateCompoundDetails,
    getAreas,
    getCites,
    updateCity,
    getUnitTypeGroups,
    getUnitViews,
    getTypeOfUnit,
    getBuilding,
    createArea,
    updateArea,
    deleteArea,
    getProperties,
    createProperty,
    updateProperty,
    deleteProperty,
    deleteBrandingImage,
    uploadBrandingImage,
    getProperty,
    getPropertyUnitTypes,
    getUnassignedUnitTypes,
    createUnitType,
    getUnitTypes,
    searchInUnitTypes,
    getUnitType,
    getUserReservations,
    updateUnitType,
    syncUnitTypeChannel,
    getUserPayments,
    deleteUnitTypeShowroomImage,
    uploadUnitTypeShowRoomImage,
    getAmenities,
    getChannelAmenities,
    getChannelRoomAmenities,
    getLeadConfig,
    searchLeads,
    getLeadById,
    createLead,
    opsReport,
    updateLead,
    changeLeadStatus,
    recordContactAttempt,
    assignLead,
    getLeadImportTemplate,
    importLeads,
    bulkAssignLeads,
    bulkAutoAssignLeads,
    markLeadDuplicate,
    getLeadTimeline,
    getLeadComments,
    createLeadComment,
    updateLeadComment,
    deleteLeadComment,
    getTicketCategories,
    getTicketStats,
    getTickets,
    getTicket,
    createTicket,
    addTicketNote,
    replyToTicket,
    categorizeTicket,
    handoffTicket,
    raiseTicketPriority,
    assignTicket,
    setTicketState,
    downloadTicketAttachment,
    searchTeams,
    getTeamById,
    createTeam,
    updateTeam,
    deleteTeam,
    getAccessControlConfig,
    getTeamMemberTree,
    searchTeamMembers,
    createTeamMember,
    updateTeamMember,
    archiveTeamMember,
    recoverTeamMember,
    getTeamMemberHistory,
    getAssignmentLimits,
    upsertAssignmentLimit,
    getAssignmentRules,
    getRuleCriteriaKeys,
    createAssignmentRule,
    updateAssignmentRule,
    deleteAssignmentRule,
    getBrokers,
    createBroker,
    updateBroker,
    deleteBroker,
    getCompanies,
    createCompany,
    updateCompany,
    deleteCompany,
    getAllUsers,
    createAdminUser,
    addUser,
    getSalesPersons,
    createSalesPerson,
    getLandlords,
    getLandlordDetail,
    upsertLandlordBankAccount,
    downloadBankDetailsTemplate,
    validateBankDetailsImport,
    applyBankDetailsImport,
    downloadBankDetailsResult,
    createAmenity,
    updateAmenity,
    deleteAmenity,
    uploadAmenityImage,
    getBlogs,
    getBlog,
    createBlog,
    updateBlog,
    publishBlog,
    unpublishBlog,
    deleteBlog,
    uploadBlogImage,
    uploadUnitTypeImage,
    deleteUnitTypeImage,
    getBookingRequests,
    updateBookingRequests,
    getBookingRequestsByQuery,
    getUserBookings,
    getBookingRequestDetails,
    acceptBookingRequest,
    validateToken,
    getReservations,
    getReservationGes,
    getCxQueue,
    updateCxQueue,
    getReservationsOperationsSearch,
    createReservationOperationMaintenance,
    searchMaintenanceRequests,
    updateMaintenanceRequest,
    getOperationDashboardSummary,
    getReservationsDashboardSummary,
    getReservationsDashboardUpdates,
    getDoubleBookings,
    getOneReservation,
    updateOneReservation,
    updateReservationGes,
    updateReservationDates,
    splitReservation,
    getOneGuest,
    updateGuest,
    deleteGuest,
    addGuest,
    resendReservationGuestEmail,
    postReservationMessage,
    getReservationActivities,
    getPaymentActivities,
    getReservationChanges,
    reservationRoomSwitch,
    updateOnePayment,
    createOnePayment,
    generatePaymentLink,
    sendPaymentLink,
    checkPaymobPaymentStatus,
    movePayment,
    createPaymentInvoice,
    uploadPaymentReceipt,
    uploadGuestDocument,
    getBookAvailability,
    getBookOffer,
    createReservations,
    createBlockDates,
    bulkUpdateBlockDates,
    createNewBookingRequest,
    updateBlockDates,
    getBlockDates,
    getUnitHistory,
    reactivateReservation,
    createUnitTypeGroup,
    updateUnitTypeGroup,
    deleteUnitTypeGroup,
    submitBookingMessage,
    uploadSponsoredImage,
    uploadDefaultImage,
    rejectBookingRequest,
    getMonthlyRevenue,
    getMonthlyRevenueWithCurrency,
    getOccupancyAndAverageDailyRate,
    getRevenuePerArea,
    getInternationAndGuest,
    getSourceOfBooking,
    getReservationsUpdates,
    getOccupancy,
    getDailyAvailabilityReport,
    getLandlordDetailedReport,
    exportLandlordDetailedReport,
    getLandlordReportFilterOptions,
    getGuestAudience,
    exportGuestAudience,
    getGuestAudienceFilterOptions,
    getHomeownerAudience,
    exportHomeownerAudience,
    getHomeownerAudienceFilterOptions,
    getOverduePayments,
    exportOverduePayments,
    getOverduePaymentsFilterOptions,
    paymentSearch,
    financialReportSearch,
    createExpenses,
    deleteExpenses,
    uploadExpense,
    createPayout,
    uploadPayout,
    previewBulkPayouts,
    createBulkPayouts,
    deletePayout,
    searchPayouts,
    getPayouts,
    searchHousekeepingOrders,
    updateHousekeepingOrder,
    assignHousekeepingOrderToSelf,
    assignHousekeepingUsers,
    createHousekeepingOrder,
    uploadHousekeepingOrderImages,
    getExpenses,
    searchUser,
    getUserLockStatus,
    unlockUser,
    refreshToken,
    getConfiguration,
    updateConfiguration,
    addPropertyManager,
    deletePropertyManager,
    getAllRooms,
    bulkUpdateUnitType,
    getAllBulkUpdateUnitTypeData,
    addPropertyManagerToReservations,
    deletePropertyManagerToReservations,
    addPropertyManagerToUnits,
    deletePropertyManagerToUnits,
    getAllReservationsFeebacks,
    getOneReservationsFeebacks,
    downloadFeedback,
    getUsers,
    getGuests,
    furnishingPackageCreate,
    furnishingPackageDelete,
    furnishingPackageUpdate,
    furnishingPackagesGet,
    furnishingRequestsGet,
    furnishingRequestCreate,
    furnishingRequestUpdate,
    furnishingRequestStatusEventsGet,
    furnishingModelsGet,
    furnishingModelCreate,
    furnishingModelUpdate,
    furnishingModelDelete,
    furnishingShowRoomsGet,
    furnishingRoomCreate,
    furnishingRoomDelete,
    furnishingRoomUpdate,
    furnishingItemSpecCreate,
    furnishingItemSpecsGet,
    furnishingItemSpecDelete,
    furnishingItemSpecUpdate,
    furnishingItemSpecImageUpdate,
    furnishingPricesCreate,
    furnishingOffersUpdate,
    furnishingOffersGet,
    furnishingOffersDelete,
    furnishingModelImageUpdate,
    furnishingPackageImageUpdate,
    furnishingRoomImageUpdate,
    furnishingUnitTypes,
    furnishingUnitTypesWebsite,
    furnishingAddOnsCreate,
    furnishingAddOnsUpdate,
    furnishingAddOnsDelete,
    furnishingAddOnsGet,
    furnishingPortfolioGet,
    furnishingPortfolioCreate,
    furnishingPortfolioDelete,
    furnishingPortfolioUpdate,
    furnishingPortfolioImageUpdate,
    furnishingPortfolioRoomsGet,
    furnishingPortfolioRoomsCreate,
    furnishingPortfolioRoomsUpdate,
    furnishingPortfolioRoomsImageUpdate,
    furnishingPortfolioReviewsGet,
    furnishingPortfolioReviewCreate,
    furnishingPortfolioReviewUpdate,
    furnishingPortfolioReviewDelete,
    getOccupancyDashboardDataset,
    calculateUnitAvailabilityDays,
    getUnitAvailabilityDaysLastRun,
    createPromotion,
    getPromotions,
    updatePromotion,
    deletePromotion,
    uploadReservationGesInspectionImages
  };

  // Auto-assign displayName to all methods
  Object.keys(apiMethods).forEach((key) => {
    const method = apiMethods[key as keyof typeof apiMethods];
    if (typeof method === 'function') {
      (method as any).displayName = key;
    }
  });

  return apiMethods;
};

// const api = debugConfig.useFixtures ? fixtureApiService : create();
const api = create();

export default api;
export type ApiType = typeof api;
